import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Package } from 'lucide-react';
import axios from 'axios';
import AnimatedSection from '../components/AnimatedSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detailed category data for each brand
const brandCategories = {
  haiwell: [
    {
      name: 'Programmable Logic Controllers (PLCs)',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/p6j3gnwq_Haiwell%20PLC.png',
      description: 'Haiwell PLCs offer powerful control with built-in IoT connectivity, making them ideal for both traditional automation and modern Industry 4.0 applications.',
      features: [
        'Multiple I/O configurations (8-256 points)',
        'Built-in Ethernet & RS485 ports',
        'Support for Modbus TCP/RTU, OPC-UA',
        'Free programming software (HaiwellHappy)',
        'Ladder logic, function blocks, structured text',
        'Real-time clock & data logging',
        'Expandable with I/O modules',
        'DIN rail mounting'
      ],
      specs: 'CPU speeds up to 0.3μs per instruction | Operating temp: -20°C to +60°C'
    },
    {
      name: 'IoT Controllers & 4G Gateways',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/gydb5hly_Iot%20Gateway.png',
      description: 'Connect plant-floor devices to the cloud with Haiwell IoT controllers, featuring built-in 4G/LTE, MQTT, and seamless integration with cloud SCADA platforms.',
      features: [
        '4G/LTE cellular connectivity',
        'MQTT protocol support',
        'Cloud platform integration (AWS, Azure, Haiwell Cloud)',
        'Remote programming & diagnostics',
        'Edge computing capabilities',
        'VPN support for secure access',
        'SMS alarm notifications',
        'Low power consumption'
      ],
      specs: 'Network: 4G LTE Cat 1/4 | Protocols: MQTT, HTTP, Modbus | Power: 12-24V DC'
    },
    {
      name: 'Cloud HMI Touchscreens',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/vt1t9uc1_D%20Series%20HMI.png',
      description: 'Modern touchscreen HMIs with cloud connectivity, enabling remote monitoring and control from anywhere via web browser or mobile app.',
      features: [
        'Screen sizes: 7" to 15.6" TFT displays',
        'Resolution up to 1920x1080 pixels',
        'Multi-touch capacitive screens',
        'Built-in web server for remote access',
        'Recipe management & data logging',
        'Alarm management with email/SMS',
        'Multi-language support',
        'USB & Ethernet connectivity'
      ],
      specs: 'Brightness: 400-500 cd/m² | Viewing angle: 170° | Lifespan: 50,000 hours'
    },
    {
      name: 'SCADA Software & Cloud Platform',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/bi5oihp8_Haiwell%20Link%20Data%20Center.png',
      description: 'Haiwell Cloud SCADA provides centralized monitoring and control of multiple sites, with real-time data visualization and historical trending.',
      features: [
        'Web-based SCADA interface',
        'Multi-site management dashboard',
        'Real-time data visualization',
        'Historical data trending & reports',
        'User access control & permissions',
        'Mobile app for iOS/Android',
        'Customizable dashboards',
        'API for third-party integration'
      ],
      specs: 'Cloud hosting or on-premise deployment | Unlimited data points | 99.9% uptime SLA'
    }
  ],
  supmea: [
    {
      name: 'Electromagnetic Flow Meters',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/hebjc3di_Flowmeters%20Pic.png',
      description: 'Supmea electromagnetic flow meters provide accurate, reliable measurement of conductive liquids with no moving parts and minimal pressure drop.',
      features: [
        'Pipe sizes: DN10 to DN2000',
        'Accuracy: ±0.5% of reading',
        'Liner materials: PTFE, rubber, PFA',
        'Electrode materials: 316L SS, Hastelloy, titanium',
        'Output: 4-20mA, pulse, HART, Modbus',
        'Bi-directional flow measurement',
        'IP67/IP68 protection rating',
        'Empty pipe detection'
      ],
      specs: 'Fluid temp: -20°C to +180°C | Pressure: up to 4.0 MPa | Power: 220VAC or 24VDC'
    },
    {
      name: 'Ultrasonic Flow Meters',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/qaoov19u_15.png',
      description: 'Non-invasive ultrasonic flow meters for clean liquids and gases, featuring clamp-on or insertion sensors with no pressure drop or moving parts.',
      features: [
        'Technologies: Transit-time, Doppler',
        'Pipe sizes: DN15 to DN6000',
        'Accuracy: ±1% to ±2% of reading',
        'Clamp-on or insertion mounting',
        'No pressure loss or flow obstruction',
        'Suitable for clean or turbid liquids',
        'Portable and fixed installation options',
        'Output: 4-20mA, pulse, RS485, HART'
      ],
      specs: 'Fluid temp: -40°C to +200°C | Power: 85-265VAC or 12-36VDC | Display: Backlit LCD'
    },
    {
      name: 'Level Transmitters & Switches',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/8bt2s8tb_Level%20Pic.png',
      description: 'Ultrasonic, radar, and hydrostatic level measurement solutions for liquids and solids in tanks, silos, and open channels.',
      features: [
        'Technologies: Ultrasonic, radar, hydrostatic',
        'Measurement range: 0.3m to 70m',
        'Accuracy: ±0.25% to ±1% FS',
        'Output: 4-20mA, HART, Modbus, Profibus',
        'Display: Graphical LCD with setup menu',
        'Non-contact measurement (ultrasonic/radar)',
        'Temperature compensation',
        'Foam & turbulence rejection'
      ],
      specs: 'Process temp: -40°C to +250°C | Pressure: up to 4.0 MPa | Power: 12-36V DC'
    },
    {
      name: 'Pressure Transmitters',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/4htt32mm_%E5%8E%8B%E5%8A%9B%E5%8F%98%E9%80%81%E5%99%A8.png',
      description: 'High-precision pressure transmitters for gauge, absolute, and differential pressure measurement in industrial processes.',
      features: [
        'Pressure ranges: 0-10kPa to 0-60MPa',
        'Accuracy: ±0.075% to ±0.25% FS',
        'Output: 4-20mA, 0-5V, 0-10V, HART',
        'Process connections: 1/4" NPT, 1/2" NPT, M20',
        'Wetted materials: 316L SS, Hastelloy',
        'Display: Optional LCD with backlight',
        'Overload protection: 2x or 3x rated pressure',
        'Explosion-proof options available'
      ],
      specs: 'Operating temp: -40°C to +125°C | Stability: ±0.1% FS/year | Protection: IP65'
    },
    {
      name: 'Supmea Liquid Analysers',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/zkxouqx1_Liquid%20Analyser%20Pic.png',
      description: 'Advanced liquid analysis instruments for pH, conductivity, dissolved oxygen, turbidity, and other critical water quality parameters in industrial processes.',
      features: [
        'Measurement types: pH, ORP, conductivity, DO, turbidity, chlorine',
        'Digital sensors with MODBUS/HART communication',
        'Automatic temperature compensation',
        'Self-diagnostic capabilities',
        'Multi-parameter analysis options',
        'Weatherproof IP65/IP67 enclosures',
        'Automatic calibration & cleaning systems',
        'Output: 4-20mA, RS485, HART protocol'
      ],
      specs: 'pH range: 0-14 | Conductivity: 0.01μS/cm to 2000mS/cm | Power: 85-265VAC or 12-36VDC'
    }
  ],
  savch: [
    {
      name: 'General Purpose VFD Inverters',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/c3od4tpu_SAVCH%20Drive%20Pic.png',
      description: 'Savch VFD inverters provide precise motor speed control, energy savings, and advanced motor protection for pumps, fans, and conveyors.',
      features: [
        'Power range: 0.75kW to 630kW',
        'Input voltage: 220V, 380V, 480V, 690V',
        'Vector control & V/F control modes',
        'Built-in PID controller',
        'Auto-tuning for motor parameters',
        'Multiple protection functions',
        'Energy-saving mode',
        'Remote control via Modbus RTU/TCP'
      ],
      specs: 'Frequency: 0-400Hz | Overload: 150% for 60s | Efficiency: >98% | IP20/IP54'
    },
    {
      name: 'High-Performance Servo Drives',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/9akj582v_SAVCH%20Servo%20Pic.png',
      description: 'AC servo drives and motors for precision motion control applications requiring high dynamic response and positioning accuracy.',
      features: [
        'Power range: 50W to 15kW',
        'Voltage: 220V single-phase, 380V three-phase',
        'Control modes: Position, speed, torque',
        'Encoder resolution: up to 23-bit (8.3M pulses/rev)',
        'Response frequency: up to 2.5kHz',
        'Communication: Pulse/direction, Modbus, EtherCAT',
        'Auto-tuning & gain optimization',
        'Electronic gearing & camming'
      ],
      specs: 'Speed range: 0-5000 rpm | Overload: 300% rated torque | Accuracy: ±0.01%'
    },
    {
      name: 'Integrated Motion Controllers',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/g5b2oi9v_SAVC%20Family%20pic.png',
      description: 'Multi-axis motion controllers for coordinated motion applications, CNC machines, and automated production lines.',
      features: [
        'Control up to 4/8/16 axes',
        'Linear & circular interpolation',
        'Electronic gearing & camming',
        'Position, speed, torque control modes',
        'Built-in PLC functionality',
        'Graphical programming interface',
        'Ethernet & fieldbus connectivity',
        'I/O expansion modules available'
      ],
      specs: 'Interpolation resolution: 1μm | Max speed: 20,000 rpm | Real-time kernel'
    }
  ],
  'yotta-control': [
    {
      name: 'Smart Distributed I/O Modules',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/p1sltw9y_Distributed%20I-O%20pic.png',
      description: 'Distributed I/O across RS485, WiFi, Ethernet and daisy-chain connectivity — purpose-built for SMART industrial IoT: Security, Manageability, Availability, Robustness and Integration.',
      features: [
        'A-10x Series RS485 I/O — robust wired fieldbus',
        'A-12x Series WiFi I/O — wireless plug-and-play',
        'A-18x Series Ethernet I/O — high-speed TCP/IP',
        'A-19x Series Daisy-Chain I/O — simplified wiring',
        'Digital input, digital output, analog I/O modules',
        'Modbus RTU / TCP protocol support',
        'DIN-rail mounting, industrial-grade enclosures',
        'Hot-swappable and expandable architecture'
      ],
      specs: 'Operating temp: -20°C to +70°C | Power: 10-30V DC | Protocols: Modbus RTU/TCP, MQTT'
    },
    {
      name: 'Controllers (A-5x Series)',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/ddhk724r_A-52x2%281%29.png',
      description: 'Compact and powerful controllers across RS485, WiFi and Ethernet — ideal for edge control, building automation, and small-to-mid industrial machines.',
      features: [
        'A-51x Series — RS485 controllers',
        'A-52x Series — WiFi wireless controllers',
        'A-53x Series — Ethernet controllers',
        'Integrated digital & analog I/O points',
        'Built-in Modbus master/slave',
        'MQTT publish/subscribe for IIoT',
        'Remote firmware updates',
        'Low-power embedded design'
      ],
      specs: 'Programming: Ladder + Function Blocks | Memory: up to 128KB program | I/O: 8-32 points onboard'
    },
    {
      name: 'HMI & Edge Computing',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/r3lrm3iy_Yotta%20HMI-Edge.png',
      description: 'From simple text panels to fanless edge-computing controllers running SCADA and Home Assistant — a full HMI stack for every deployment size.',
      features: [
        'ATP-2 Text Panel — low-cost operator interface',
        'A-9H HMI PC — industrial human-machine interface computer',
        'A-9N GUI Fanless Edge Controller — IPC-grade reliability',
        'HIPC Home Assistant IPC — next-gen SCADA platform',
        'Web-based dashboards with responsive UI',
        'Multi-language support',
        'Recipe & data logging',
        'IP65-rated front panels'
      ],
      specs: 'Display: 4.3" to 15.6" TFT | Storage: industrial SSD | OS: Linux-based / Home Assistant OS'
    },
    {
      name: 'IoT Gateway (9101 / 9103 Series)',
      image: 'https://customer-assets.emergentagent.com/job_automation-hub-228/artifacts/1jer6oi5_IOT%20Gateway%2092x%281%29.png',
      description: 'Protocol-converting IoT gateways that bridge OT and IT — taking legacy serial, Modbus and proprietary data streams into the cloud securely.',
      features: [
        'IOT-Gateway-9101 & 9103 (latest releases)',
        'Modbus RTU / TCP → MQTT / HTTPS translation',
        'Secure TLS-encrypted cloud transport',
        'Edge data buffering & store-and-forward',
        'OPC UA client/server capability',
        'VPN-ready for remote deployments',
        'Dual-SIM 4G/LTE options',
        'Small DIN-rail form factor'
      ],
      specs: 'Protocols: Modbus, MQTT, OPC UA, HTTPS | WAN: Ethernet + optional 4G | DIN rail mount'
    }
  ]
};

const BrandDetail = () => {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrand = async () => {
      try {
        const response = await axios.get(`${API}/brands/${slug}`);
        setBrand(response.data);
      } catch (error) {
        console.error('Error fetching brand:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrand();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-white mb-4">Brand not found</h2>
          <Link to="/brands" className="text-ewss-cyan hover:underline">Back to Brands</Link>
        </div>
      </div>
    );
  }

  const accentColor =
    brand.color === '#00E5FF' || brand.color === '#1791FF' ? 'ewss-cyan' :
    brand.color === '#EF4444' ? 'ewss-red' :
    brand.color === '#10B981' ? 'ewss-green' :
    'ewss-orange';

  const categories = brandCategories[slug] || [];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-ewss-navy via-ewss-navy to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/brands" 
            className="inline-flex items-center text-gray-400 hover:text-ewss-cyan mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Brands
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="mb-6">
              {brand.logo_url ? (
                <div className="flex items-center justify-center mb-4">
                  <img
                    src={brand.logo_url}
                    alt={brand.name}
                    className="max-h-32 md:max-h-40 object-contain"
                    data-testid="brand-detail-logo"
                  />
                </div>
              ) : (
                <h1 className={`text-5xl md:text-6xl font-bold text-${accentColor} mb-4`}>
                  {brand.logo_placeholder}
                </h1>
              )}
              <p className="text-2xl text-gray-300 mb-6">{brand.tagline}</p>
            </div>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-8">{brand.description}</p>
            
            {/* Key Features */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {brand.features.slice(0, 4).map((feature, idx) => (
                <span key={idx} className={`px-4 py-2 bg-${accentColor}/10 text-${accentColor} text-sm font-semibold rounded-full border border-${accentColor}/30`}>
                  {feature}
                </span>
              ))}
            </div>

            <Link
              to="/contact"
              className={`inline-flex items-center px-8 py-4 bg-${accentColor} text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(23,145,255,0.5)] transition-all duration-300`}
              data-testid="request-catalog-btn"
            >
              <Package className="mr-2" size={20} />
              Request Product Catalog
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-4">
              Product <span className={`text-${accentColor}`}>Categories</span>
            </h2>
            <p className="text-center text-gray-400 text-lg mb-16 max-w-2xl mx-auto">
              Explore our complete range of {brand.name} products and solutions
            </p>
          </AnimatedSection>

          <div className="space-y-20">
            {categories.map((category, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                  {/* Category Image */}
                  <div className="flex-1 w-full">
                    <div className="rounded-xl overflow-hidden border border-ewss-cyan/30 bg-white/5 flex items-center justify-center">
                      <img 
                        src={category.image}
                        alt={category.name}
                        className="w-[115%] h-auto object-contain"
                      />
                    </div>
                  </div>

                  {/* Category Details */}
                  <div className="flex-1">
                    <div className={`w-16 h-16 bg-${accentColor}/10 rounded-xl flex items-center justify-center text-${accentColor} mb-6`}>
                      <Package className="w-8 h-8" />
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-4">
                      {category.name}
                    </h3>
                    <p className="text-gray-300 text-lg mb-6">
                      {category.description}
                    </p>

                    {/* Features */}
                    <div className="mb-6">
                      <h4 className={`text-lg font-semibold text-${accentColor} mb-4`}>Key Features:</h4>
                      <div className="grid grid-cols-1 gap-3">
                        {category.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start">
                            <CheckCircle2 className={`text-${accentColor} mr-3 mt-1 flex-shrink-0`} size={18} />
                            <span className="text-gray-400 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Specifications */}
                    {category.specs && (
                      <div className={`p-4 bg-${accentColor}/5 border border-${accentColor}/20 rounded-lg mb-6`}>
                        <p className="text-gray-400 text-sm">
                          <span className={`font-semibold text-${accentColor}`}>Specifications: </span>
                          {category.specs}
                        </p>
                      </div>
                    )}

                    {/* CTA */}
                    <Link
                      to="/contact"
                      className={`inline-flex items-center px-6 py-3 border-2 border-${accentColor} text-${accentColor} font-semibold rounded-lg hover:bg-${accentColor} hover:text-ewss-navy transition-all duration-300`}
                      data-testid={`request-quote-${index}`}
                    >
                      Request Quote
                    </Link>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-b from-ewss-navy to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className={`bg-gradient-to-br from-${accentColor}/10 to-transparent border border-${accentColor}/30 rounded-2xl p-8 md:p-12 text-center`}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Order {brand.name} Products?
              </h2>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                Contact EWSS Automation for pricing, technical specifications, and delivery timelines.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/resources"
                  className={`px-8 py-4 border-2 border-${accentColor} text-${accentColor} font-semibold rounded-lg hover:bg-${accentColor}/10 transition-all duration-300`}
                  data-testid="view-resources-btn"
                >
                  Technical Resources
                </Link>
                <Link
                  to="/contact"
                  className={`px-8 py-4 bg-${accentColor} text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(23,145,255,0.5)] transition-all duration-300`}
                  data-testid="contact-sales-btn"
                >
                  Contact Sales Team
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default BrandDetail;
